import React, { useRef, useState } from 'react';
import { Vote, FileText, MessageSquare, FileSignature, ArrowUp, ArrowDown } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '@shared/ui/Button';
import RelatedEntitySelector, { EntityLink } from '../RelatedEntitySelector';
import RichTextEditor from '../../RichTextEditor';
import HelpTrigger from '../../../help/components/HelpTrigger';
import api from '../../../lib/api';
import type { OutreachType, TemplateDefinition } from '../../../types/outreach';
import { AnalyticsIcon, DeleteIcon, UploadIcon } from '../../icons';

type OutreachImageEntry = NonNullable<TemplateDefinition['images']>[number];

const MAX_IMAGES = 5;
const MAX_IMAGE_BYTES = 10 * 1024 * 1024; // 10MB — mirrors the server filter
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];

// Same relative base the rest of the app uses; staff-only surface, so the
// serve-proxy fallback URL derived from a key is fine here.
const API_BASE = '/api';

interface Props {
  name: string;
  description: string;
  /** Set in Step 0 (template or from-scratch kind); displayed here as an inert chip */
  type: OutreachType | '';
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  workspaceId?: string; // Optional - only needed for entity linking
  entityLinks?: EntityLink[]; // Optional - only for outreach items, not templates
  onEntityLinksChange?: (links: EntityLink[]) => void; // Optional - only for outreach items
  /** definition.images — display order; only storage keys + alt persist */
  images?: OutreachImageEntry[];
  onImagesChange?: (images: OutreachImageEntry[]) => void;
}

const TYPE_DISPLAY: Record<string, { label: string; icon: React.FC<{ className?: string }> }> = {
  campaign: { label: 'Campaign', icon: Vote },
  survey: { label: 'Survey', icon: FileText },
  poll: { label: 'Poll', icon: AnalyticsIcon },
  message: { label: 'Message', icon: MessageSquare },
  petition: { label: 'Petition', icon: FileSignature },
};

const WizardStep1BasicInfo: React.FC<Props> = ({
  name,
  description,
  type,
  onNameChange,
  onDescriptionChange,
  workspaceId,
  entityLinks,
  onEntityLinksChange,
  images,
  onImagesChange
}) => {
  const typeDisplay = type ? TYPE_DISPLAY[type] : null;
  const TypeIcon = typeDisplay?.icon;

  // Runtime key → display URL map from upload responses. Images loaded from an
  // existing item aren't in the map; their URL derives from the key instead.
  const [uploadedUrls, setUploadedUrls] = useState<Record<string, string>>({});
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const imageList = images || [];
  const displayUrlFor = (key: string) => uploadedUrls[key] || `${API_BASE}/files/serve/${key}`;

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Allow re-selecting the same file after a remove
    e.target.value = '';
    if (!file || !onImagesChange) return;

    if (imageList.length >= MAX_IMAGES) {
      toast.error(`Maximum ${MAX_IMAGES} images per outreach`);
      return;
    }
    if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }
    if (file.size > MAX_IMAGE_BYTES) {
      toast.error('Image is too large — maximum size is 10MB');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      // The upload is workspace-scoped and org-admin gated on the server
      // (OUTREACH_CONTRIBUTOR_READ_ONLY_PRD.md §5.3). Text fields go before the
      // file so multer has parsed them when the handler runs.
      if (workspaceId) formData.append('workspace_id', workspaceId);
      formData.append('image', file);
      // The shared api client defaults Content-Type to application/json, and
      // an explicit default beats axios's FormData auto-detection — without
      // this header the body isn't multipart and multer sees no file. Same
      // pattern as every other upload call site (e.g. OrganizationSettingsPage).
      const response = await api.post('/outreach-unified/images', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const { key, url } = response.data?.data || {};
      if (!key) {
        toast.error('Failed to upload image');
        return;
      }
      setUploadedUrls(prev => ({ ...prev, [key]: url || `${API_BASE}/files/serve/${key}` }));
      onImagesChange([...imageList, { key }]);
    } catch (err: any) {
      console.error('Failed to upload outreach image:', err);
      toast.error(err.response?.data?.error || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (index: number) => {
    onImagesChange?.(imageList.filter((_, i) => i !== index));
  };

  const moveImage = (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= imageList.length) return;
    const next = [...imageList];
    [next[index], next[target]] = [next[target], next[index]];
    onImagesChange?.(next);
  };

  const setImageAlt = (index: number, alt: string) => {
    const next = imageList.map((img, i) => {
      if (i !== index) return img;
      // Drop the key entirely when cleared — the server rejects unknown/empty shapes gracefully
      return alt ? { ...img, alt } : { key: img.key };
    });
    onImagesChange?.(next);
  };

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-text-primary dark:text-text-dark-primary mb-2">
          Let&apos;s set up your {typeDisplay ? typeDisplay.label.toLowerCase() : 'outreach'}
        </h2>
        <p className="text-text-secondary dark:text-text-dark-secondary">
          Give it a name and a description
        </p>
        {typeDisplay && TypeIcon && (
          <div className="mt-3 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300 text-sm font-medium">
            <TypeIcon className="w-4 h-4" />
            {typeDisplay.label}
          </div>
        )}
      </div>

      {/* Outreach Name */}
      <div>
        <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
          Outreach Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="e.g., Support HB-1234, Community Survey, etc."
          className="input w-full"
          autoFocus
        />
      </div>

      {/* Images (PRD OUTREACH_UPDATES_2026-09 §B.4.4) */}
      {onImagesChange && (
        <div>
          <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
            Images <span className="text-text-tertiary dark:text-text-dark-tertiary text-xs">(Optional)</span>
          </label>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-3">
            Images appear on your public page between the title and description.
          </p>

          {imageList.length > 0 && (
            <ul className="space-y-3 mb-3">
              {imageList.map((img, index) => (
                <li
                  key={img.key}
                  className="flex items-center gap-3 p-2 rounded-lg border border-border-light dark:border-border-dark"
                >
                  <img
                    src={displayUrlFor(img.key)}
                    alt={img.alt || ''}
                    className="w-24 h-16 object-cover rounded flex-shrink-0"
                  />
                  <input
                    type="text"
                    value={img.alt || ''}
                    onChange={(e) => setImageAlt(index, e.target.value)}
                    placeholder="Describe this image for screen readers"
                    maxLength={300}
                    className="input flex-1 min-w-0 text-sm"
                    aria-label={`Alt text for image ${index + 1}`}
                  />
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={ArrowUp}
                      onClick={() => moveImage(index, -1)}
                      disabled={index === 0}
                      aria-label={`Move image ${index + 1} up`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={ArrowDown}
                      onClick={() => moveImage(index, 1)}
                      disabled={index === imageList.length - 1}
                      aria-label={`Move image ${index + 1} down`}
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon={DeleteIcon}
                      onClick={() => removeImage(index)}
                      aria-label={`Remove image ${index + 1}`}
                    />
                  </div>
                </li>
              ))}
            </ul>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp"
            className="hidden"
            onChange={handleFileSelected}
          />
          <Button
            variant="outline"
            icon={UploadIcon}
            loading={uploading}
            disabled={imageList.length >= MAX_IMAGES}
            onClick={() => fileInputRef.current?.click()}
          >
            {uploading ? 'Uploading...' : 'Add Image'}
          </Button>
          {imageList.length >= MAX_IMAGES && (
            <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-2">
              Maximum {MAX_IMAGES} images per outreach.
            </p>
          )}
        </div>
      )}

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-4">
          {type === 'petition' ? (
            <>Petition Statement <span className="text-red-500">*</span> <HelpTrigger contentKey="outreach-petition" size="sm" /></>
          ) : (
            <>Description <span className="text-text-tertiary dark:text-text-dark-tertiary text-xs">(Optional)</span></>
          )}
        </label>
        <RichTextEditor
          value={description}
          onChange={onDescriptionChange}
          placeholder={type === 'petition'
            ? 'The statement people are signing — make the case clearly...'
            : 'Brief description of what this outreach is for...'}
          maxLength={1800}
        />
        <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mt-2">
          {type === 'petition'
            ? 'This statement is the petition — it appears prominently at the top of the signing page'
            : 'This description will appear at the top of your outreach form'}
        </p>
      </div>

      {/* Related Entities - Only show for outreach items, not templates */}
      {workspaceId && entityLinks !== undefined && onEntityLinksChange && (
        <div>
          <label className="block text-sm font-medium text-text-secondary dark:text-text-dark-secondary mb-2">
            Related to <span className="text-text-tertiary dark:text-text-dark-tertiary text-xs">(Optional)</span>
          </label>
          <p className="text-xs text-text-tertiary dark:text-text-dark-tertiary mb-3">
            Link this outreach to bills, legislators, calendar events, candidates, and/or ballot initiatives from your workspace. Related items must be tracked in this workspace. A link to more detail about the related content will be included with the outreach.
          </p>
          <RelatedEntitySelector
            workspaceId={workspaceId}
            selectedLinks={entityLinks}
            onLinksChange={onEntityLinksChange}
          />
        </div>
      )}
    </div>
  );
};

export default WizardStep1BasicInfo;
